import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import { useFavicon } from "@/components/Logo";
import { MobileCtaBar } from "@/components/MobileCtaBar";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { NotesFab } from "@/components/NotesFab";

const Content = lazy(() => import("@/pages/Content"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Results = lazy(() => import("@/pages/Results"));
const About = lazy(() => import("@/pages/About"));
const Visit = lazy(() => import("@/pages/Visit"));
const Admin = lazy(() => import("@/pages/Admin"));

const PageFallback = () => <main className="min-h-screen" aria-busy="true" />;

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LenisRoot = ({ children }) => {
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
  return children;
};

const Shell = () => {
  const { pathname } = useLocation();
  useFavicon();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="relative min-h-screen bg-cream">
      {!isAdmin && (
        <div className="orbs" aria-hidden="true">
          <div className="orb orb-1" /><div className="orb orb-2" /><div className="orb orb-3" /><div className="orb orb-4" />
        </div>
      )}
      <div className="relative z-10">
      {!isAdmin && <Navbar />}
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/content" element={<Content />} />
          <Route path="/explore" element={<Content />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/results" element={<Results />} />
          <Route path="/about" element={<About />} />
          <Route path="/visit" element={<Visit />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </Suspense>
      {!isAdmin && <Footer />}
      {!isAdmin && <MobileCtaBar />}
      {!isAdmin && <WhatsAppFab />}
      {!isAdmin && <NotesFab />}
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LenisRoot>
        <ScrollManager />
        <Shell />
        <Toaster position="bottom-center" toastOptions={{ style: { background: "#1B2340", color: "#FFFFFF", border: "1px solid rgba(14,90,71,0.4)" } }} />
      </LenisRoot>
    </BrowserRouter>
  );
}

export default App;
